import React from 'react';
import { cn } from '@/lib/cn';

export type BadgeType = 'accent' | 'neutral' | 'alert';

export interface BadgeProps {
  /** Badge type - Accent (indigo), Neutral (gray), or Alert (red) */
  type?: BadgeType;
  /** Whether to show count number or just a dot */
  count?: number;
  /** Whether to show a white border around the badge */
  border?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function Badge({ type = 'accent', count = 0, border = false, className }: BadgeProps) {
  const showCount = count > 0;
  const isAccent = type === 'accent';
  const isNeutral = type === 'neutral';
  const isAlert = type === 'alert';

  // Get background color based on type
  const getBackgroundColor = () => {
    if (isAccent) return 'bg-accent';
    if (isNeutral) return 'bg-tertiary';
    if (isAlert) return 'bg-alert';
    return 'bg-accent';
  };

  // Get text color based on type (only for count badges)
  const getTextColor = () => {
    if (isAccent || isAlert) return 'text-inverted';
    if (isNeutral) return 'text-secondary';
    return 'text-inverted';
  };

  if (showCount) {
    // Badge with count number
    return (
      <div
        className={cn(
          // Base styles
          'flex items-center justify-center rounded-full overflow-hidden',
          // Size: height 20px, min-width 20px
          'h-5 min-w-5',
          // Padding: 4px horizontal
          'px-1',
          // Background color
          getBackgroundColor(),
          // Border: 2px white when border prop is true
          border && 'border-2 border-white',
          className,
        )}
      >
        <span
          className={cn(
            // Typography: Caption/High emphasis (12px semibold)
            'text-caption-high text-center whitespace-nowrap',
            // Text color
            getTextColor(),
          )}
        >
          {count}
        </span>
      </div>
    );
  }

  // Badge as dot (8px size, border adds extra size on top)
  return (
    <div
      className={cn(
        // Base styles
        'rounded-full overflow-hidden',
        // Size: Always 8px (size-2) for the dot itself
        'size-2',
        // Use box-content so border adds to the size rather than being included in it
        'box-content',
        // Background color
        getBackgroundColor(),
        // Border: 2px white when border prop is true (adds extra size on top of 8px)
        border && 'border-2 border-white',
        className,
      )}
    />
  );
}

export default Badge;
